/** TLS certificate pin helpers for the system TLS card (backend:
 *  core.transport, `System.tls_pin`). Display-only — the server does the
 *  actual fingerprint comparison and trust decision. */

/** Days before not_after that the console starts warning. */
export const EXPIRY_WARN_DAYS = 30

function hexPairs(sha256: string): string[] {
  return sha256.replace(/:/g, '').toUpperCase().match(/.{1,2}/g) ?? []
}

/** Full fingerprint as upper-case colon pairs (`AB:CD:…`), the form
 *  browsers, `openssl x509 -fingerprint` and device admin pages print. */
export function formatFingerprint(sha256: string): string {
  return hexPairs(sha256).join(':')
}

/** First and last four pairs — for the at-a-glance pin summary only. */
export function shortFingerprint(sha256: string): string {
  const pairs = hexPairs(sha256)
  if (pairs.length <= 8) return pairs.join(':')
  return `${pairs.slice(0, 4).join(':')}…${pairs.slice(-4).join(':')}`
}

export type CertExpiry = 'expired' | 'soon' | 'ok'

/** Where a certificate's not_after sits relative to now, or null when
 *  the date can't be parsed. */
export function certExpiry(notAfter: string, now: number = Date.now()): CertExpiry | null {
  const t = Date.parse(notAfter)
  if (!notAfter || Number.isNaN(t)) return null
  if (t <= now) return 'expired'
  if (t - now <= EXPIRY_WARN_DAYS * 86400_000) return 'soon'
  return 'ok'
}

/** The CN of an RFC 4514 subject string (`CN=host,O=…`), unescaped. */
export function subjectCommonName(subject: string): string | null {
  // Split on commas not preceded by a backslash escape.
  for (const part of subject.split(/(?<!\\),/)) {
    const m = /^\s*CN=(.*)$/i.exec(part)
    if (m) return m[1].replace(/\\(.)/g, '$1').trim()
  }
  return null
}

function urlHost(baseUrl: string): string | null {
  try {
    return new URL(baseUrl).hostname.replace(/^\[|\]$/g, '').toLowerCase() || null
  } catch {
    return null
  }
}

/** A CN that plainly names a host: an IP literal or a dotted DNS name
 *  (optionally a leading wildcard). Vendor CNs like "Network Optix" or
 *  "localhost" say nothing about the address, so they are never flagged. */
function looksLikeHost(cn: string): boolean {
  if (cn.includes(':')) return /^[0-9a-f:.]+$/i.test(cn) // IPv6
  return /^(\*\.)?[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(cn)
}

function hostMatches(cn: string, host: string): boolean {
  if (cn === host) return true
  if (cn.startsWith('*.')) {
    const dot = host.indexOf('.')
    return dot > 0 && host.slice(dot + 1) === cn.slice(2)
  }
  return false
}

/** When a pinned certificate's CN clearly names a different host than the
 *  system's base URL (e.g. the URL was changed after pinning), both hosts;
 *  otherwise null. A hint only — the pin is checked by fingerprint, not
 *  by name, and SANs aren't in the summary. */
export function certHostMismatch(subject: string, baseUrl: string): { certHost: string; urlHost: string } | null {
  const cn = subjectCommonName(subject)?.toLowerCase()
  const host = urlHost(baseUrl)
  if (!cn || !host || !looksLikeHost(cn)) return null
  return hostMatches(cn, host) ? null : { certHost: cn, urlHost: host }
}
